import { Flex, Heading } from '@utilitywarehouse/hearth-react';
import clsx from 'clsx';
import { uniqueId } from 'lodash';
import { ReactNode, useEffect } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import { NavBar } from '@components/NavBar';
import { UpdatingModal } from '@components/UpdatingModal';
import { paths } from '@constants/paths';
import { RemoveDialog } from '@features/RemoveDialog';
import { ProductType } from '@generated/gql/graphql';
import { useBreakpoints } from '@utils/useBreakpoints';

import { MobileFAQ } from '../MobileFAQ';
import { MobilePromoNote } from '../MobilePromoNote';
import { OfferBanner } from '../OfferBanner';
import { useMobileContext } from '../Provider/Context';
import { SaveChangesModal } from '../SaveChangesModal';
import { MAX_CHOSEN_SIMS } from '../constants';
import { useMobileTracking } from '../hooks/useMobileTracking';
import { useResetMobileRemoteFormState } from '../hooks/useResetMobileRemoteFormState';
import { AddSimTile } from './AddSimTile';
import { SimTile } from './SimTile';
import { SimTileEmpty, SimTileSkeleton } from './SimTilePlaceholder';
import './styles.scss';

interface TileWrapperProps {
  children: ReactNode;
}

const TileWrapper = ({ children }: TileWrapperProps) => (
  <Flex
    asChild
    direction="column"
    flex={{
      mobile: '1 1 100%',
      tablet: '1 1 calc(50% - var(--h-space-300))',
      desktop: '1',
    }}
  >
    {children}
  </Flex>
);

const navigateToSim = (navigate: NavigateFunction, id?: string) => {
  if (id) {
    return navigate(`${paths.mobile}/sim/${id}/settings`);
  }

  return navigate(`${paths.mobile}/sim/add`);
};

interface EmptyTilesProps {
  loading: boolean;
  emptyTilesCount: number;
  isMobile: boolean;
}

const EmptyTiles = ({
  loading,
  emptyTilesCount,
  isMobile,
}: EmptyTilesProps) => {
  if (loading) {
    return Array.from({ length: MAX_CHOSEN_SIMS }, () => (
      <TileWrapper key={uniqueId('loading-tile')}>
        <li>
          <SimTileSkeleton />
        </li>
      </TileWrapper>
    ));
  }

  if (emptyTilesCount <= 0 || isMobile) {
    return null;
  }

  return Array.from({ length: emptyTilesCount }, () => (
    <TileWrapper key={uniqueId('empty-tile')}>
      <li>
        <SimTileEmpty />
      </li>
    </TileWrapper>
  ));
};

const ChosenSimsSection = () => {
  const navigate = useNavigate();
  const reset = useResetMobileRemoteFormState();

  const {
    mobileLoading,
    isRemovingService,
    isRemoveDialogOpen,
    removedSimName,
    selectedSims,
    removeSim,
    removeSimSubmit,
    removeService,
    removeSimDialogClose,
    submitMobile,
  } = useMobileContext();

  // Clear mobile-specific fields from the remote FORM_STATE on mount.
  // This component only renders when the SIM list is visible, meaning
  // FormSync (and its useSyncFormField) is not in the DOM. This ensures
  // a single owner of the FORM_STATE at any given time, avoiding update
  // loops. Without this, navigating to add/edit a SIM would cause
  // useSyncFormField to hydrate the empty form with stale data from the
  // previous SIM interaction.
  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { trackMobileSaved } = useMobileTracking(selectedSims);

  const isLoading = mobileLoading || isRemovingService;

  const { breakpointsDown } = useBreakpoints();
  const isMobile = breakpointsDown.md;
  const chosenSimsCount = selectedSims?.length ?? 0;
  // Subtract number of SIMs plus add button
  const emptyTilesCount = MAX_CHOSEN_SIMS - (chosenSimsCount + 1);
  const hasSelectedSims = chosenSimsCount > 0;

  const showUnlimitedMaxPromoLegal = selectedSims.some(
    (sim) => (sim.selectedTariff.discounts?.length ?? 0) > 0
  );

  const handleSaveMobile = () => {
    trackMobileSaved();
    submitMobile(() => navigate(paths.bundle));
  };

  return (
    <>
      <Flex
        direction="column"
        className={clsx('wrapper', 'chosenSimsSection')}
        spacing="2xl"
      >
        <OfferBanner />
        <Flex direction="column" spacing="lg">
          <Heading size="lg">Add up to four SIMs</Heading>
          <Flex asChild spacing="xl" wrap="wrap">
            <ul className="chosenSimsSection__list">
              {!isLoading &&
                selectedSims.map((sim) => (
                  <TileWrapper key={sim.id}>
                    <li>
                      <SimTile
                        sim={sim}
                        onEdit={() => navigateToSim(navigate, sim.id)}
                        onDelete={() => removeSim(sim.id)}
                      />
                    </li>
                  </TileWrapper>
                ))}
              {!isLoading && chosenSimsCount < MAX_CHOSEN_SIMS && (
                <TileWrapper>
                  <li>
                    <AddSimTile onClick={() => navigateToSim(navigate)} />
                  </li>
                </TileWrapper>
              )}
              <EmptyTiles
                loading={isLoading}
                emptyTilesCount={emptyTilesCount}
                isMobile={isMobile}
              />
            </ul>
          </Flex>
          {showUnlimitedMaxPromoLegal && <MobilePromoNote />}
        </Flex>
      </Flex>
      <MobileFAQ />
      {/* Controlled by useMobileSubmit hook */}
      <UpdatingModal
        heading="Updating your mobile service..."
        productType={ProductType.Mobile}
        isRemoving={isRemovingService}
      />
      {/* Opened by the page-level back button when there are unsaved changes */}
      <SaveChangesModal onSave={handleSaveMobile} />
      <RemoveDialog
        productType={ProductType.Mobile}
        productName={removedSimName}
        isOpen={isRemoveDialogOpen}
        onClose={removeSimDialogClose}
        onRemove={removeSimSubmit}
      />
      <NavBar
        nextLabel="Add to Bundle"
        onNext={handleSaveMobile}
        nextDisabled={isLoading}
        hasRemoveButton={hasSelectedSims}
        onRemove={removeService}
      />
    </>
  );
};

export { ChosenSimsSection };
